'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { PageLoader } from '@/components/shared/BrandedLoadingSpinner';

export interface AiAnalysisOverlayProps {
  accent: string;
}

/**
 * Full-screen overlay shown while the Gemini receipt AI is analyzing the
 * captured photo(s). Rendered via a portal at document.body so it sits above
 * the camera modal.
 */
export function AiAnalysisOverlay({ accent }: AiAnalysisOverlayProps) {
  const content = (
    <PageLoader
      message="Analyzing receipt with AI..."
      subMessage="Extracting vendor, amount, line items, and suggested coding"
      primaryColor={accent}
      // AI analysis can take 30-60s on slow connections. Suppress the
      // "Clear Cache & Reload" button so the user doesn't nuke their auth
      // token and in-progress receipt data during a normal long wait.
      suppressClearCacheButton
    />
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}

export interface SubmittingOverlayProps {
  /** Loading phase key from useCaptureSession ('analyzing' | 'compressing' | 'saving' | null) */
  loadingPhase: string | null;
  accent: string;
}

const PHASE_LABELS: Record<string, string> = {
  analyzing: 'Analyzing receipt...',
  compressing: 'Compressing photo...',
  saving: 'Saving expense...',
};

/**
 * Full-screen overlay shown while the capture session is submitting the
 * expense to the intake API. Rendered via a portal at document.body.
 */
export function SubmittingOverlay({ loadingPhase, accent }: SubmittingOverlayProps) {
  const content = (
    <PageLoader
      message={loadingPhase ? PHASE_LABELS[loadingPhase] : 'Processing...'}
      subMessage="Please wait"
      primaryColor={accent}
      // Submission involves AI analysis + upload + save. Suppress the
      // clear-cache button so the user doesn't lose in-progress data.
      suppressClearCacheButton
    />
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}
